import type { PaymentMethod, Project } from '@bcl/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { extractErrorMessage } from '@/features/auth/error-message';
import { InvoiceDownloadButton } from './invoice-download-button';
import {
  initiatePayment,
  listPaymentsForProject,
  type InitiatePaymentResponse,
} from './payments-api';

const methodLabels: Record<PaymentMethod, string> = {
  carte: 'Carte bancaire (paiement immédiat)',
  virement: 'Virement bancaire (validation manuelle)',
};

function missingPrerequisites(
  project: Project,
  documentsCount: number,
): string[] {
  const missing: string[] = [];
  if (!project.info) missing.push('Compléter les informations du projet');
  if (!project.fiberQuestions) missing.push('Répondre aux questions fibre');
  if (!project.packCode) missing.push('Choisir une offre');
  if (documentsCount === 0) missing.push('Déposer au moins un document');
  return missing;
}

type PriceRange = { ht: number; ttc: number };

const PACK_DEFAULT_PRICE: Record<string, PriceRange> = {
  serenite_fibre: { ht: 340, ttc: 408 },
  essentiel_fibre: { ht: 529, ttc: 634.8 },
  confort_fibre: { ht: 799, ttc: 958.8 },
  remise_en_conformite_sav: { ht: 150, ttc: 180 },
};

export function PaymentSection({
  project,
  documentsCount,
}: {
  project: Project;
  documentsCount: number;
}) {
  const projectId = project.id;
  const [method, setMethod] = useState<PaymentMethod>('carte');
  const [error, setError] = useState<string | null>(null);
  const [lastInit, setLastInit] = useState<InitiatePaymentResponse | null>(null);

  const payments = useQuery({
    queryKey: ['payments', projectId],
    queryFn: () => listPaymentsForProject(projectId),
  });

  const defaultPrice =
    PACK_DEFAULT_PRICE[project.packCode ?? ''] ?? { ht: 0, ttc: 0 };

  const initMut = useMutation({
    mutationFn: () =>
      initiatePayment({
        projectId,
        method,
        amountHt: defaultPrice.ht,
        amountTtc: defaultPrice.ttc,
      }),
    onSuccess: (res) => {
      setLastInit(res);
      if (res.method === 'carte') {
        window.location.assign(res.redirectUrl);
      }
    },
    onError: (e) => setError(extractErrorMessage(e)),
  });

  const missing = missingPrerequisites(project, documentsCount);
  const blocked = missing.length > 0;
  const alreadyPaid = (payments.data ?? []).some((p) => p.status === 'paye');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Paiement</CardTitle>
        <CardDescription>
          Une fois votre paiement encaissé, votre dossier est automatiquement
          transmis pour vérification par BCL Telecom.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {blocked && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <div className="flex items-center gap-2 font-medium">
              <Lock className="h-4 w-4" aria-hidden="true" />
              Terminez les étapes précédentes pour pouvoir payer&nbsp;:
            </div>
            <ul className="mt-2 list-inside list-disc space-y-1">
              {missing.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        {!blocked && !alreadyPaid && (
          <>
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p>
                Montant à régler&nbsp;:{' '}
                <strong>{defaultPrice.ttc.toFixed(2)} € TTC</strong>{' '}
                <span className="text-muted-foreground">
                  ({defaultPrice.ht.toFixed(2)} € HT)
                </span>
              </p>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium">Mode de paiement</p>
              <div className="space-y-2">
                {(Object.keys(methodLabels) as PaymentMethod[]).map((k) => (
                  <label
                    key={k}
                    className={`flex items-center gap-3 rounded-md border p-3 text-sm cursor-pointer ${
                      method === k ? 'border-bcl-blue bg-bcl-blue/5' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment-method"
                      value={k}
                      checked={method === k}
                      onChange={() => setMethod(k)}
                    />
                    <span>{methodLabels[k]}</span>
                  </label>
                ))}
              </div>
            </div>

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}

            <Button
              onClick={() => initMut.mutate()}
              disabled={initMut.isPending}
              size="lg"
            >
              {initMut.isPending
                ? 'Préparation du paiement…'
                : method === 'carte'
                  ? 'Payer maintenant'
                  : 'Recevoir les coordonnées de virement'}
            </Button>
          </>
        )}

        {lastInit?.method === 'virement' && (
          <div className="rounded-md border bg-muted p-3 text-sm">
            <p className="font-medium">Coordonnées bancaires</p>
            <p>IBAN : {lastInit.bank.iban}</p>
            <p>BIC : {lastInit.bank.bic}</p>
            <p>Bénéficiaire : {lastInit.bank.beneficiary}</p>
            <p className="mt-2">
              Référence à indiquer dans le virement :{' '}
              <strong>{lastInit.reference}</strong>
            </p>
          </div>
        )}

        {payments.data && payments.data.length > 0 && (
          <ul className="space-y-2 text-sm">
            {payments.data.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-3 rounded-md border p-2"
              >
                <span>
                  {methodLabels[p.method]} · {p.amountTtc.toFixed(2)} €
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">{p.status}</span>
                  {p.status === 'paye' && (
                    <InvoiceDownloadButton paymentId={p.id} />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
